import { Box, Typography, Button, Card, CardContent, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import dayjs from 'dayjs';
import { TourFormData } from '../ScheduleTourFlow';
import icon from '../../../../public/favicon.svg';

interface ConfirmationStepProps {
    formData: TourFormData;
}

function ConfirmationStep({ formData }: ConfirmationStepProps) {
    const formattedDate = dayjs(formData.tourDate).format('MMMM D, YYYY');

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                maxWidth: '600px',
                mx: 'auto',
                textAlign: 'center',
            }}
        >
            {/* Success Icon */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box component="img" src={icon} sx={{ height: 100, filter: 'brightness(0%)' }} />
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
            </Box>

            {/* Confirmation Message */}
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Your Tour is Scheduled!
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                Thank you for scheduling a tour with us. We're excited to show you around and answer any questions you may have.
            </Typography>

            {/* Tour Details Card */}
            <Card
                sx={{
                    width: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    boxShadow: 3,
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Tour Details
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
                        {/* School */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <LocationOnIcon sx={{ color: 'primary.main' }} />
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    School
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {formData.schoolName}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Date */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CalendarTodayIcon sx={{ color: 'primary.main' }} />
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Date
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {formattedDate}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Time */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <AccessTimeIcon sx={{ color: 'primary.main' }} />
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Time
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {formData.tourTime}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Contact */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <PersonIcon sx={{ color: 'primary.main' }} />
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Contact
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {formData.parentFirstName} {formData.parentLastName}
                                </Typography>
                                <Typography variant="body2">{formData.phoneNumber}</Typography>
                                <Typography variant="body2">{formData.email}</Typography>
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Next Steps */}
            <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    What's Next?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    • You will receive a confirmation email shortly
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    • A member of our team will reach out to confirm your tour
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    • Please arrive 5-10 minutes early on the day of your tour
                </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    href="/"
                >
                    Return to Home
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    fullWidth
                    href="/our-schools"
                >
                    View All Schools
                </Button>
            </Box>
        </Box>
    );
}

export default ConfirmationStep;
